// src/utils/ctiFilters.js
// Utility functions for filtering CTI data based on risk status

/**
 * Filter CTI data to include only items with allowed risk statuses
 * Excludes 'corrigé' items from scenario generation as per requirements
 */

// Allowed statuses for scenario generation (excluding 'corrigé' and 'non_pertinent')
export const ALLOWED_STATUSES_FOR_SCENARIOS = ['non_corrige', 'accepté', 'risque_accepte'];

// All possible statuses in the system
export const ALL_CTI_STATUSES = ['non_corrige', 'corrige', 'accepté', 'risque_accepte', 'non_pertinent'];

// Status labels for UI display
export const STATUS_LABELS = {
  'non_corrige': 'Non Corrigé',
  'corrige': 'Corrigé',
  'accepté': 'Accepté',
  'risque_accepte': 'Risque Accepté',
  'non_pertinent': 'Non Pertinent'
};

// Status labels for English report output
export const STATUS_LABELS_EN = {
  'non_corrige': 'Not Fixed',
  'corrige': 'Fixed',
  'accepté': 'Accepted',
  'risque_accepte': 'Risk Accepted',
  'non_pertinent': 'Not Relevant'
};

// Status colors for UI display
export const STATUS_COLORS = {
  'non_corrige': 'text-red-600 bg-red-50 border-red-200',
  'corrige': 'text-green-600 bg-green-50 border-green-200',
  'accepté': 'text-yellow-600 bg-yellow-50 border-yellow-200',
  'risque_accepte': 'text-yellow-600 bg-yellow-50 border-yellow-200'
};

/**
 * Filter vulnerabilities based on allowed statuses for scenario generation
 * @param {Array} vulnerabilities - Array of vulnerability objects
 * @param {Array} allowedStatuses - Array of allowed status values (default: ALLOWED_STATUSES_FOR_SCENARIOS)
 * @returns {Object} Filtered vulnerabilities with counts
 */
export const filterVulnerabilitiesForScenarios = (vulnerabilities = [], allowedStatuses = ALLOWED_STATUSES_FOR_SCENARIOS) => {
  const originalCount = vulnerabilities.length;
  
  const filtered = vulnerabilities.filter(vuln => {
    const status = vuln.status || 'non_pertinent'; // Default to non_pertinent if no status
    return allowedStatuses.includes(status);
  });

  return {
    vulnerabilities: filtered,
    originalCount,
    filteredCount: filtered.length,
    excludedCount: originalCount - filtered.length
  };
};

/**
 * Filter attack techniques based on allowed statuses for scenario generation
 * @param {Array} techniques - Array of technique objects
 * @param {Array} allowedStatuses - Array of allowed status values (default: ALLOWED_STATUSES_FOR_SCENARIOS)
 * @returns {Object} Filtered techniques with counts
 */
export const filterTechniquesForScenarios = (techniques = [], allowedStatuses = ALLOWED_STATUSES_FOR_SCENARIOS) => {
  const originalCount = techniques.length;
  
  const filtered = techniques.filter(tech => {
    const status = tech.status || 'non_pertinent'; // Default to non_pertinent if no status
    return allowedStatuses.includes(status);
  });

  return {
    techniques: filtered,
    originalCount,
    filteredCount: filtered.length,
    excludedCount: originalCount - filtered.length
  };
};

/**
 * Filter complete CTI data for scenario generation
 * @param {Object} ctiData - Complete CTI data object
 * @param {Array} allowedStatuses - Array of allowed status values (default: ALLOWED_STATUSES_FOR_SCENARIOS)
 * @returns {Object} Filtered CTI data with metadata
 */
export const filterCTIDataForScenarios = (ctiData, allowedStatuses = ALLOWED_STATUSES_FOR_SCENARIOS) => {
  if (!ctiData) {
    return {
      vulnerabilities: [],
      techniques: [],
      assets: [],
      filterMetadata: {
        vulnerabilities: { originalCount: 0, filteredCount: 0, excludedCount: 0 },
        techniques: { originalCount: 0, filteredCount: 0, excludedCount: 0 },
        totalOriginalCount: 0,
        totalFilteredCount: 0,
        totalExcludedCount: 0
      }
    };
  }

  // Filter vulnerabilities
  const vulnResult = filterVulnerabilitiesForScenarios(ctiData.vulnerabilities, allowedStatuses);
  
  // Filter techniques
  const techResult = filterTechniquesForScenarios(ctiData.techniques, allowedStatuses);

  // Filter assets and update their vulnerabilities/techniques
  const filteredAssets = (ctiData.assets || []).map(asset => {
    const assetVulnResult = filterVulnerabilitiesForScenarios(asset.vulnerabilities || [], allowedStatuses);
    const assetTechResult = filterTechniquesForScenarios(asset.attackTechniques || [], allowedStatuses);

    return {
      ...asset,
      vulnerabilities: assetVulnResult.vulnerabilities,
      attackTechniques: assetTechResult.techniques,
      // Add filtering metadata to asset
      filterMetadata: {
        vulnerabilities: {
          originalCount: assetVulnResult.originalCount,
          filteredCount: assetVulnResult.filteredCount,
          excludedCount: assetVulnResult.excludedCount
        },
        techniques: {
          originalCount: assetTechResult.originalCount,
          filteredCount: assetTechResult.filteredCount,
          excludedCount: assetTechResult.excludedCount
        }
      }
    };
  });

  // Calculate total metadata
  const totalOriginalCount = vulnResult.originalCount + techResult.originalCount;
  const totalFilteredCount = vulnResult.filteredCount + techResult.filteredCount;
  const totalExcludedCount = vulnResult.excludedCount + techResult.excludedCount;

  return {
    ...ctiData,
    vulnerabilities: vulnResult.vulnerabilities,
    techniques: techResult.techniques,
    assets: filteredAssets,
    filterMetadata: {
      vulnerabilities: {
        originalCount: vulnResult.originalCount,
        filteredCount: vulnResult.filteredCount,
        excludedCount: vulnResult.excludedCount
      },
      techniques: {
        originalCount: techResult.originalCount,
        filteredCount: techResult.filteredCount,
        excludedCount: techResult.excludedCount
      },
      totalOriginalCount,
      totalFilteredCount,
      totalExcludedCount,
      allowedStatuses,
      excludedStatuses: ALL_CTI_STATUSES.filter(status => !allowedStatuses.includes(status))
    }
  };
};

/**
 * Filter attack paths with CTI data for scenario generation
 * @param {Array} attackPathsWithCTI - Array of attack paths with CTI data
 * @param {Array} allowedStatuses - Array of allowed status values (default: ALLOWED_STATUSES_FOR_SCENARIOS)
 * @returns {Object} Filtered attack paths with metadata
 */
export const filterAttackPathsForScenarios = (attackPathsWithCTI = [], allowedStatuses = ALLOWED_STATUSES_FOR_SCENARIOS) => {
  const filteredPaths = attackPathsWithCTI.map(path => {
    const vulnResult = filterVulnerabilitiesForScenarios(path.vulnerabilities || [], allowedStatuses);
    const techResult = filterTechniquesForScenarios(path.attackTechniques || [], allowedStatuses);

    return {
      ...path,
      vulnerabilities: vulnResult.vulnerabilities,
      attackTechniques: techResult.techniques,
      vulnerabilitiesCount: vulnResult.filteredCount,
      techniquesCount: techResult.filteredCount,
      // Add filtering metadata
      filterMetadata: {
        vulnerabilities: {
          originalCount: vulnResult.originalCount,
          filteredCount: vulnResult.filteredCount,
          excludedCount: vulnResult.excludedCount
        },
        techniques: {
          originalCount: techResult.originalCount,
          filteredCount: techResult.filteredCount,
          excludedCount: techResult.excludedCount
        }
      }
    };
  });

  // Calculate overall statistics
  const totalVulnerabilities = filteredPaths.reduce((sum, path) => sum + (path.vulnerabilities?.length || 0), 0);
  const totalTechniques = filteredPaths.reduce((sum, path) => sum + (path.attackTechniques?.length || 0), 0);
  const originalVulnerabilities = filteredPaths.reduce((sum, path) => sum + (path.filterMetadata?.vulnerabilities?.originalCount || 0), 0);
  const originalTechniques = filteredPaths.reduce((sum, path) => sum + (path.filterMetadata?.techniques?.originalCount || 0), 0);

  return {
    attackPathsWithCTI: filteredPaths,
    filterMetadata: {
      totalVulnerabilities,
      totalTechniques,
      originalVulnerabilities,
      originalTechniques,
      excludedVulnerabilities: originalVulnerabilities - totalVulnerabilities,
      excludedTechniques: originalTechniques - totalTechniques,
      allowedStatuses,
      excludedStatuses: ALL_CTI_STATUSES.filter(status => !allowedStatuses.includes(status))
    }
  };
};

/**
 * Get filtering summary for UI display
 * @param {Object} filterMetadata - Filter metadata from filtering functions
 * @returns {Object} Summary object for UI display
 */
export const getFilteringSummary = (filterMetadata) => {
  if (!filterMetadata) return null;

  const {
    totalOriginalCount = 0,
    totalFilteredCount = 0,
    totalExcludedCount = 0,
    vulnerabilities = {},
    techniques = {},
    allowedStatuses = [],
    excludedStatuses = []
  } = filterMetadata;

  return {
    summary: {
      total: {
        original: totalOriginalCount,
        filtered: totalFilteredCount,
        excluded: totalExcludedCount,
        percentage: totalOriginalCount > 0 ? Math.round((totalFilteredCount / totalOriginalCount) * 100) : 0
      },
      vulnerabilities: {
        original: vulnerabilities.originalCount || 0,
        filtered: vulnerabilities.filteredCount || 0,
        excluded: vulnerabilities.excludedCount || 0,
        percentage: vulnerabilities.originalCount > 0 ? Math.round((vulnerabilities.filteredCount / vulnerabilities.originalCount) * 100) : 0
      },
      techniques: {
        original: techniques.originalCount || 0,
        filtered: techniques.filteredCount || 0,
        excluded: techniques.excludedCount || 0,
        percentage: techniques.originalCount > 0 ? Math.round((techniques.filteredCount / techniques.originalCount) * 100) : 0
      }
    },
    statuses: {
      allowed: allowedStatuses.map(status => ({
        id: status,
        label: STATUS_LABELS[status] || status,
        color: STATUS_COLORS[status] || 'text-gray-600'
      })),
      excluded: excludedStatuses.map(status => ({
        id: status,
        label: STATUS_LABELS[status] || status,
        color: STATUS_COLORS[status] || 'text-gray-600'
      }))
    }
  };
};

/**
 * Check if CTI data has sufficient content for scenario generation after filtering
 * @param {Object} filteredCTIData - Filtered CTI data
 * @param {Object} options - Options for validation
 * @returns {Object} Validation result
 */
export const validateFilteredCTIData = (filteredCTIData, options = {}) => {
  const {
    minVulnerabilities = 1,
    minTechniques = 1,
    requireBoth = false // If true, requires both vulnerabilities AND techniques
  } = options;

  const vulnCount = filteredCTIData.vulnerabilities?.length || 0;
  const techCount = filteredCTIData.techniques?.length || 0;

  let isValid = false;
  let message = '';

  if (requireBoth) {
    isValid = vulnCount >= minVulnerabilities && techCount >= minTechniques;
    if (!isValid) {
      message = `Données CTI insuffisantes après filtrage. Requis: ${minVulnerabilities} vulnérabilité(s) ET ${minTechniques} technique(s). Trouvé: ${vulnCount} vulnérabilité(s) et ${techCount} technique(s).`;
    }
  } else {
    isValid = vulnCount >= minVulnerabilities || techCount >= minTechniques;
    if (!isValid) {
      message = `Données CTI insuffisantes après filtrage. Requis: ${minVulnerabilities} vulnérabilité(s) OU ${minTechniques} technique(s). Trouvé: ${vulnCount} vulnérabilité(s) et ${techCount} technique(s).`;
    }
  }

  return {
    isValid,
    message,
    counts: {
      vulnerabilities: vulnCount,
      techniques: techCount,
      total: vulnCount + techCount
    },
    requirements: {
      minVulnerabilities,
      minTechniques,
      requireBoth
    }
  };
};
